/**
 * Tracked changes: typing becomes a PROPOSAL rather than a change.
 *
 * WHY A CONTROLLED DOCUMENT NEEDS THIS AND A BLOG POST DOES NOT. A procedure's review cycle is
 * exactly this shape -- a reviewer proposes, an owner disposes, and the record has to show both
 * acts and who did them. Without it a reviewer either edits the procedure directly, which loses the
 * distinction between "suggested" and "agreed", or argues about it in email, which loses it
 * entirely.
 *
 * THE REST OF THE SYSTEM WAS ALREADY BUILT FOR THIS, which is why the marks are named what they are
 * and not something prettier:
 *
 *   * `resolved(body)` in cres-aws-t11 produces the ACCEPTED view -- insertions kept with the mark
 *     stripped, deletions dropped -- and the full-text index is built from that, so search answers
 *     about the agreed text rather than the argument about it.
 *   * `count_suggestions(body)` counts what is outstanding, denormalised onto
 *     DOCUMENT_CONTENT.OPEN_SUGGESTIONS and summed onto the revision.
 *   * t1 refuses to send the document for approval while any remain (CC-OAPR-SUGG): "a signature
 *     has to be against one version of the text, not two."
 *
 * So these two mark names are a CONTRACT with the server, not a local choice.
 *
 * AND UNTIL NOW THE EDITOR DID NOT KNOW THEM, which was a live hazard rather than a missing
 * feature: ProseMirror silently discards marks its schema has never heard of, so a body carrying
 * suggestions, opened and saved, would have come back with every suggestion quietly accepted and
 * OPEN_SUGGESTIONS at zero -- which would also have unlocked submission. Registering the marks is
 * worth doing on its own, before anything can create such a body.
 *
 * THEY SURVIVE COLLABORATION FOR FREE. Marks live in the Y.Doc like any other formatting, so a
 * suggestion made in one window appears in the other through the same CRDT updates as the text.
 */
import { Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey, Selection } from "@tiptap/pm/state";
import { Mark as PMMark } from "@tiptap/pm/model";
import { ySyncPluginKey } from "y-prosemirror";

export type SuggestionMode = "editing" | "suggesting" | "viewing";

/** Author colours, matching the collaboration carets so one person is one colour throughout. */
export const AUTHOR_COLOURS = ["#5ABCED", "#E8833A", "#43A047", "#8E6FD8", "#D81B60", "#00897B"];

export function colourFor(userId: string): string {
    let hash = 0;
    for (let i = 0; i < userId.length; i += 1) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
    return AUTHOR_COLOURS[Math.abs(hash) % AUTHOR_COLOURS.length];
}

/** Who made a suggestion, carried on the mark so the document is self-describing. */
type Author = { userId: string; userName: string };

const attributes = {
    /**
     * WHO, BY USID, and the name beside it.
     *
     * THE NAME IS DENORMALISED ON PURPOSE, as every *_AUDIT_LOG in this schema does it: a
     * suggestion has to still say who made it after that person is renamed or leaves, and a
     * suggestion attributed to a USID nobody can resolve is not attribution.
     */
    userId: { default: "", parseHTML: (el: any) => el.getAttribute("data-user") ?? "",
              renderHTML: (attrs: any) => ({ "data-user": attrs.userId }) },
    userName: { default: "", parseHTML: (el: any) => el.getAttribute("data-user-name") ?? "",
                renderHTML: (attrs: any) => ({ "data-user-name": attrs.userName }) },
    /** ISO, set by the browser that made it. Display only -- the audit log is the record of record. */
    at: { default: "", parseHTML: (el: any) => el.getAttribute("data-at") ?? "",
          renderHTML: (attrs: any) => ({ "data-at": attrs.at }) },
};

function title(kind: string, attrs: any): string {
    const when = attrs.at ? new Date(attrs.at).toLocaleString() : "";
    return `${kind} by ${attrs.userName || "someone"}${when ? ` · ${when}` : ""}`;
}

export const Insertion = Mark.create({
    name: "insertion",
    // INCLUSIVE, so typing on at the end of your own insertion extends it rather than starting a
    // second one -- otherwise a sentence typed in one go becomes a dozen suggestions to accept.
    inclusive: true,
    addAttributes: () => attributes,
    parseHTML: () => [{ tag: "ins" }, { tag: "span[data-suggestion='insertion']" }],
    renderHTML({ HTMLAttributes }) {
        return ["ins", mergeAttributes(HTMLAttributes, {
            "data-suggestion": "insertion",
            class: "suggestion suggestion-insertion",
            style: `--author: ${colourFor(HTMLAttributes["data-user"] || "")}`,
            title: title("Inserted", {
                userName: HTMLAttributes["data-user-name"], at: HTMLAttributes["data-at"] }),
        }), 0];
    },
});

export const Deletion = Mark.create({
    name: "deletion",
    // NOT INCLUSIVE: typing immediately after struck text is new text, not more deletion.
    inclusive: false,
    addAttributes: () => attributes,
    parseHTML: () => [{ tag: "del" }, { tag: "span[data-suggestion='deletion']" }],
    renderHTML({ HTMLAttributes }) {
        return ["del", mergeAttributes(HTMLAttributes, {
            "data-suggestion": "deletion",
            class: "suggestion suggestion-deletion",
            style: `--author: ${colourFor(HTMLAttributes["data-user"] || "")}`,
            title: title("Deleted", {
                userName: HTMLAttributes["data-user-name"], at: HTMLAttributes["data-at"] }),
        }), 0];
    },
});

export const suggestionPluginKey = new PluginKey("parkourSuggestions");

/**
 * The plugin that turns edits into suggestions.
 *
 * TWO HALVES, AND THEY WORK DIFFERENTLY ON PURPOSE.
 *
 * INSERTIONS are marked AFTER the fact, in appendTransaction. Typing, pasting, autocorrect and the
 * input rules all arrive as replace steps that add content, and catching them in one place means
 * none of them can slip through unmarked -- which an interception per keystroke would eventually
 * let happen.
 *
 * DELETIONS are intercepted BEFORE the fact, in handleKeyDown, because a deletion that has already
 * happened cannot be marked: the text is gone. Backspace and Delete instead apply the deletion mark
 * and move the cursor over it.
 *
 * ONE EXCEPTION, AND IT IS THE ONE THAT MAKES THE MODE USABLE: deleting your OWN pending insertion
 * really deletes it. Retracting something you have just proposed should not leave a struck-through
 * ghost that somebody else has to reject -- it was never part of the document.
 */
export function suggestionPlugin(get: () => { mode: SuggestionMode; author: Author }) {
    return new Plugin({
        key: suggestionPluginKey,

        appendTransaction(transactions, _oldState, newState) {
            const { mode, author } = get();
            if (mode !== "suggesting") return null;
            if (!transactions.some(one => one.docChanged)) return null;
            // OUR OWN WORK IS NOT RE-MARKED. Accepting or rejecting changes the document, and
            // marking those changes as new suggestions would make resolution impossible.
            if (transactions.some(one => one.getMeta(suggestionPluginKey))) return null;

            /**
             * SOMEBODY ELSE'S TYPING IS NOT OUR SUGGESTION.
             *
             * y-prosemirror replays remote CRDT updates as ordinary transactions that insert
             * content, and without this they were indistinguishable from local typing -- so every
             * character a collaborator typed would have come back marked as an insertion by US.
             * Not merely wrong attribution: their text would become a proposal nobody made, in a
             * document where attribution is the point of the feature.
             *
             * It also covers the INITIAL SYNC, which is the whole document arriving at once.
             */
            if (transactions.some(one => one.getMeta(ySyncPluginKey)?.isChangeOrigin)) return null;

            /**
             * AND LOADING A DOCUMENT IS NOT EDITING IT.
             *
             * This is the one that actually destroyed a document, and the audit trail named it:
             * OPEN_SUGGESTIONS went 14 → 167 in four seconds. Seeding calls setContent with the
             * whole saved body, which arrives here as one enormous insertion -- so a document
             * opened while Suggesting became a single proposal covering every word in it.
             *
             * From there the damage is one keystroke away in either direction: Reject all deletes
             * the entire procedure, and Backspace takes the "retract your own insertion" path --
             * which really deletes, because by then every word IS your own insertion.
             */
            if (transactions.some(one => one.getMeta("parkourSeeding"))) return null;

            const insertion = newState.schema.marks.insertion;
            const deletion = newState.schema.marks.deletion;
            if (!insertion) return null;

            const ranges: { from: number; to: number }[] = [];
            transactions.forEach(transaction => {
                transaction.steps.forEach((step, index) => {
                    const map = transaction.mapping.maps[index];
                    map.forEach((_fromA, _toA, fromB, toB) => {
                        if (toB <= fromB) return;                       // nothing was inserted
                        // Through the REST of the mapping, so the range is valid in the new doc.
                        let from = fromB;
                        let to = toB;
                        for (let later = index + 1; later < transaction.mapping.maps.length; later += 1) {
                            from = transaction.mapping.maps[later].map(from, -1);
                            to = transaction.mapping.maps[later].map(to, 1);
                        }
                        if (to > from) ranges.push({ from, to });
                    });
                });
            });
            if (ranges.length === 0) return null;

            const tr = newState.tr;

            /**
             * ONE MARK PER RUN, NOT PER KEYSTROKE, and this was wrong first time round.
             *
             * Each character typed is its own transaction, so creating a fresh mark each time gave
             * every character a different `at` -- and ProseMirror merges adjacent marks only when
             * their attributes are IDENTICAL. Fourteen characters became fourteen <ins> elements
             * and fourteen separate things for a reviewer to accept. A sentence somebody typed in
             * one go is one suggestion.
             *
             * So: reuse the insertion mark immediately before this range when it is the same
             * author's, which carries its original timestamp forward -- the moment the run STARTED,
             * which is the more useful of the two anyway.
             */
            const markFor = (at: number) => {
                // resolve(at).marks() IS THE CHARACTER BEFORE `at`, which is the previous keystroke
                // of this run. It was resolve(at - 1) first time round -- one character too far
                // back, landing on the original text -- so every keystroke found no neighbour and
                // made its own mark. Twenty characters, twenty suggestions. It LOOKED fixed in
                // testing only because that test typed next to a run left by the run before it.
                const existing = insertion.isInSet(newState.doc.resolve(at).marks());
                if (existing && existing.attrs.userId === author.userId) return existing;
                return insertion.create({ ...author, at: new Date().toISOString() });
            };

            ranges.forEach(({ from, to }) => {
                const end = Math.min(to, newState.doc.content.size);
                if (end <= from) return;
                // TEXT ONLY. A mark on a paragraph boundary is meaningless and ProseMirror will
                // not keep it; stepping the range skips the gaps between nodes.
                newState.doc.nodesBetween(from, end, (node, pos) => {
                    if (!node.isText) return;
                    const start = Math.max(from, pos);
                    const finish = Math.min(end, pos + node.nodeSize);
                    if (finish <= start) return;
                    // NOT OVER TEXT ALREADY STRUCK. Retyping inside a deletion is a new
                    // insertion, and both marks on one character is a contradiction.
                    if (deletion && node.marks.some(m => m.type === deletion)) return;
                    tr.addMark(start, finish, markFor(start));
                });
            });
            if (!tr.steps.length) return null;
            return tr.setMeta(suggestionPluginKey, "mark-insertions").setMeta("addToHistory", false);
        },

        props: {
            handleKeyDown(view, event) {
                const { mode, author } = get();
                if (mode !== "suggesting") return false;
                if (event.key !== "Backspace" && event.key !== "Delete") return false;

                const { state } = view;
                const deletion = state.schema.marks.deletion;
                const insertion = state.schema.marks.insertion;
                if (!deletion) return false;

                let { from, to } = state.selection;
                if (from === to) {
                    // A caret, not a selection: the character behind or ahead of it.
                    const target = event.key === "Backspace" ? from - 1 : from + 1;
                    if (target < 0 || target > state.doc.content.size) return true;
                    from = Math.min(from, target);
                    to = Math.max(from, target);
                }
                if (to <= from) return true;

                const tr = state.tr;
                // SAME REASONING AS INSERTIONS: holding Backspace through a sentence is one
                // deletion, not one per character.
                //
                // THE TWO KEYS LOOK IN OPPOSITE DIRECTIONS, which is the whole subtlety.
                // Backspace walks leftwards, so the run it is extending lies AFTER the range --
                // nodeAfter. Delete walks rightwards, so the run lies before it, which is what
                // resolve(from).marks() returns. Using one lookup for both gives a fresh mark on
                // every keystroke in one of the two directions, and only in one.
                const neighbour = event.key === "Backspace"
                    ? deletion.isInSet(state.doc.resolve(Math.min(to, state.doc.content.size)).nodeAfter?.marks ?? [])
                    : deletion.isInSet(state.doc.resolve(Math.max(0, from)).marks());
                const mark = (neighbour && neighbour.attrs.userId === author.userId)
                    ? neighbour
                    : deletion.create({ ...author, at: new Date().toISOString() });
                let marked = false;

                // Collected first, applied after: removing content inside a nodesBetween walk
                // invalidates the positions it is still handing out.
                const strike: { from: number; to: number }[] = [];
                const remove: { from: number; to: number }[] = [];

                state.doc.nodesBetween(from, to, (node, pos) => {
                    if (!node.isText) return;
                    const start = Math.max(from, pos);
                    const finish = Math.min(to, pos + node.nodeSize);
                    if (finish <= start) return;
                    const own = insertion && node.marks.find(
                        m => m.type === insertion && m.attrs.userId === author.userId);
                    // RETRACTING YOUR OWN PROPOSAL REALLY DELETES IT. It was never part of the
                    // document, so leaving a struck-through ghost for somebody else to reject
                    // would be asking them to rule on a change that no longer exists.
                    if (own) remove.push({ from: start, to: finish });
                    else strike.push({ from: start, to: finish });
                });

                strike.forEach(range => { tr.addMark(range.from, range.to, mark); marked = true; });
                [...remove].reverse().forEach(range => { tr.delete(range.from, range.to); marked = true; });

                if (!marked) return true;                 // nothing to do, but do not delete either
                // THE CURSOR MOVES PAST THE STRUCK TEXT rather than staying in it, so holding
                // Backspace walks back through a sentence as it does in any editor.
                const caret = event.key === "Backspace" ? tr.mapping.map(from) : tr.mapping.map(to);
                // Selection.near, not the selection's own constructor: a NodeSelection or a
                // CellSelection has no `near`, and reaching through `.constructor` to find one is
                // how a Backspace inside a table cell throws instead of striking a word.
                tr.setSelection(Selection.near(tr.doc.resolve(caret)));
                view.dispatch(tr.setMeta(suggestionPluginKey, "mark-deletion"));
                return true;
            },
        },
    });
}

// ---------------------------------------------------------------- resolution

type Found = { from: number; to: number; mark: PMMark; kind: "insertion" | "deletion" };

/** The suggestion the cursor is standing in, or null. */
export function suggestionAt(editor: any): Found | null {
    if (!editor) return null;
    const { state } = editor;
    const { from } = state.selection;
    const found = findAround(state, from);
    return found;
}

function findAround(state: any, position: number): Found | null {
    for (const kind of ["insertion", "deletion"] as const) {
        const type = state.schema.marks[kind];
        if (!type) continue;
        const $pos = state.doc.resolve(position);
        const mark = type.isInSet($pos.marks()) || type.isInSet($pos.nodeAfter?.marks ?? []);
        if (!mark) continue;
        // WIDENED TO THE WHOLE RUN. A suggestion is the sentence somebody proposed, not the
        // character the cursor happens to be on, and accepting half of one is not a thing.
        let from = position;
        let to = position;
        while (from > 0 && type.isInSet(state.doc.resolve(from).marks())) from -= 1;
        while (to < state.doc.content.size
               && type.isInSet(state.doc.resolve(to).marks())) to += 1;
        return { from, to, mark: mark as PMMark, kind };
    }
    return null;
}

/** Every suggestion in the document, in order. */
export function allSuggestions(editor: any): Found[] {
    const out: Found[] = [];
    if (!editor) return out;
    const { state } = editor;
    (["insertion", "deletion"] as const).forEach(kind => {
        const type = state.schema.marks[kind];
        if (!type) return;
        state.doc.descendants((node: any, pos: number) => {
            if (!node.isText) return;
            const mark = node.marks.find((m: any) => m.type === type);
            if (mark) out.push({ from: pos, to: pos + node.nodeSize, mark, kind });
        });
    });
    return out.sort((a, b) => a.from - b.from);
}

/**
 * Accept or reject one suggestion.
 *
 * ACCEPT AN INSERTION = keep the words, drop the mark. REJECT ONE = delete the words.
 * ACCEPT A DELETION = delete the words. REJECT ONE = keep them, drop the mark.
 *
 * Flagged with the plugin's own meta so the resulting change is not itself marked as a new
 * suggestion -- otherwise resolving one would create another and the count would never reach zero.
 */
export function resolveSuggestion(editor: any, found: Found, accept: boolean) {
    if (!editor || !found) return;
    const { state } = editor;
    const tr = state.tr;
    const removes = (found.kind === "insertion") ? !accept : accept;
    if (removes) tr.delete(found.from, found.to);
    else tr.removeMark(found.from, found.to, found.mark.type);
    editor.view.dispatch(tr.setMeta(suggestionPluginKey, "resolve"));
}

/** Accept or reject everything, back to front so earlier positions stay valid. */
export function resolveAll(editor: any, accept: boolean) {
    if (!editor) return;
    const all = allSuggestions(editor);
    if (!all.length) return;
    const { state } = editor;
    const tr = state.tr;
    [...all].reverse().forEach(found => {
        const removes = (found.kind === "insertion") ? !accept : accept;
        if (removes) tr.delete(found.from, found.to);
        else tr.removeMark(found.from, found.to, found.mark.type);
    });
    editor.view.dispatch(tr.setMeta(suggestionPluginKey, "resolve-all"));
}
